import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams, GridRowParams, GridActionsCellItem } from '@mui/x-data-grid';
import { Button, Box, Typography, Alert, Snackbar, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';

import { DataTable } from '../components/DataTable';
import { MeshForm } from '../components/meshes/MeshForm';
import { MeshMembersDialog } from '../components/meshes/MeshMembersDialog';
import { meshesApi } from '../api/services';
import { extractApiError } from '../utils/apiErrors';
import type { Mesh } from '../api/config';

// Meshes (sc-6059): org-scoped groups of interoperating branch locations that
// share inventory. Membership is edited on the location itself (LocationForm's
// Mesh dropdown); this page manages the groups.
export const MeshesPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedMesh, setSelectedMesh] = React.useState<Mesh | undefined>();
  const [membersMesh, setMembersMesh] = React.useState<Mesh | undefined>();
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);

  const queryClient = useQueryClient();

  const { data: meshes = [], isLoading, error } = useQuery({
    queryKey: ['meshes'],
    queryFn: () => meshesApi.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: meshesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meshes'] });
      setSnackbar({ message: 'Mesh created successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to create mesh'), severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: { name: string } }) =>
      meshesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meshes'] });
      setSnackbar({ message: 'Mesh updated successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to update mesh'), severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: meshesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meshes'] });
      setSnackbar({ message: 'Mesh deleted successfully', severity: 'success' });
    },
    onError: (error) => {
      // Deleting a mesh with member locations is refused server-side.
      setSnackbar({ message: extractApiError(error, 'Failed to delete mesh'), severity: 'error' });
    },
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 35, resizable: true },
    { field: 'name', headerName: 'Name', width: 250, resizable: true },
    {
      field: 'organization',
      headerName: 'Organization',
      width: 250,
      valueGetter: (params: GridRenderCellParams<Mesh>) => params.row?.organization?.name || '',
      resizable: true,
    },
    {
      field: 'locations',
      headerName: 'Locations',
      // Fixed width (no flex) so the columns pack left and Actions sits next
      // to the content instead of across a mostly-empty table. Names stay on
      // one line; an over-long name ellipsizes, full name on hover.
      width: 520,
      sortable: false,
      filterable: false,
      // String value so the toolbar's CSV export / quick filter see names,
      // not a stringified object array. Display comes from renderCell.
      valueGetter: (params: GridRenderCellParams<Mesh>) =>
        (params.row?.locations || []).map((l) => l.name).join(', '),
      renderCell: (params: GridRenderCellParams<Mesh>) => {
        const locations = params.row?.locations || [];
        if (locations.length === 0) {
          return <Typography variant="body2" color="text.secondary">No locations</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5, minWidth: 0, width: '100%' }}>
            {locations.map((l) => (
              <Tooltip key={l.id} title={l.name} enterDelay={700}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={l.active ? {} : { color: 'text.disabled' }}
                >
                  {l.name}{l.active ? '' : ' (inactive)'}
                </Typography>
              </Tooltip>
            ))}
          </Box>
        );
      },
      resizable: true,
    },
  ];

  const customActions = (params: GridRowParams) => [
    <GridActionsCellItem
      key="members"
      icon={<GroupIcon />}
      label="Manage Members"
      onClick={() => setMembersMesh(params.row as Mesh)}
      showInMenu={false}
    />,
  ];

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedMesh(undefined);
    createMutation.reset();
    updateMutation.reset();
  };

  const mutationError = createMutation.error || updateMutation.error;

  const handleSubmit = (data: { name: string; organization_id: number }) => {
    createMutation.reset();
    updateMutation.reset();
    if (selectedMesh) {
      updateMutation.mutate({ id: selectedMesh.id, data: { name: data.name } });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (id: string) => {
    const mesh = meshes.find((m) => m.id.toString() === id);
    if (mesh) {
      setSelectedMesh(mesh);
      setIsFormOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this mesh? Meshes with member locations cannot be deleted.')) {
      deleteMutation.mutate(id);
    }
  };

  if (error) {
    return <Alert severity="error">Error loading meshes</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Meshes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsFormOpen(true)}
        >
          Add Mesh
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Groups of interoperating branches that share inventory. Manage members with the
        group action on each row, or from the location's edit form.
      </Typography>

      <DataTable
        rows={meshes}
        columns={columns}
        loading={isLoading}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
        customActions={customActions}
        getRowId={(row: Mesh) => row.id.toString()}
        getRowHeight={() => 'auto'}
        dataGridSx={{ '& .MuiDataGrid-row': { minHeight: '52px !important' } }}
        fullHeight
      />

      {membersMesh && (
        <MeshMembersDialog
          open={true}
          onClose={() => setMembersMesh(undefined)}
          mesh={membersMesh}
        />
      )}

      <MeshForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        initialData={selectedMesh}
        error={mutationError ? extractApiError(mutationError) : undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Box>
          {snackbar && (
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar(null)}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          )}
        </Box>
      </Snackbar>
    </Box>
  );
};
